import React from 'react';
import { Link } from 'react-router-dom';

function HowWeReview() {
  return (
    <div className="page review-page">
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <span className="page-badge">Our Process</span>
            <h1>How We Review Casinos</h1>
            <p>Rigorous, unbiased evaluations to help you find the best UK casinos</p>
          </div>
        </div>
      </section>

      <section className="page-content">
        <div className="container">
          <div className="review-intro">
            <p className="lead">
              At SlotsHub UK, we take casino reviews seriously. Our team of experts evaluates every 
              casino using a comprehensive methodology designed to give you accurate, useful information. 
              Here's exactly how we do it.
            </p>
          </div>

          <div className="review-criteria">
            <div className="criteria-card">
              <div className="criteria-number">1</div>
              <div className="criteria-content">
                <h2>Licensing & Regulation</h2>
                <div className="criteria-weight">Weight: 25%</div>
                <p>
                  The first and most important check. We verify that every casino holds a valid licence 
                  from the UK Gambling Commission (UKGC). This is non-negotiable – if a casino isn't 
                  UKGC-licensed, it won't appear on our site.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Valid UKGC licence status on the Gambling Commission register</li>
                  <li>Any regulatory actions or licence conditions</li>
                  <li>Additional licences from other respected jurisdictions</li>
                  <li>History of compliance and any past issues</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">2</div>
              <div className="criteria-content">
                <h2>Security & Fairness</h2>
                <div className="criteria-weight">Weight: 20%</div>
                <p>
                  We examine how casinos protect your data and ensure games are fair. Player safety 
                  is paramount, and we only recommend sites with robust security measures.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>SSL encryption (HTTPS) for all transactions</li>
                  <li>RNG (Random Number Generator) certification from independent labs</li>
                  <li>Published Return to Player (RTP) percentages</li>
                  <li>Data protection policies and GDPR compliance</li>
                  <li>Responsible gambling tool availability and effectiveness</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">3</div>
              <div className="criteria-content">
                <h2>Game Selection</h2>
                <div className="criteria-weight">Weight: 15%</div>
                <p>
                  We evaluate the variety and quality of games available. A good casino should offer 
                  something for everyone, from slots enthusiasts to table game purists.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Total number of games available</li>
                  <li>Variety of game types (slots, table games, live casino, etc.)</li>
                  <li>Software providers and their reputation</li>
                  <li>Game quality, graphics, and user experience</li>
                  <li>Mobile game availability and performance</li>
                  <li>Exclusive or unique game offerings</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">4</div>
              <div className="criteria-content">
                <h2>Bonuses & Promotions</h2>
                <div className="criteria-weight">Weight: 15%</div>
                <p>
                  We analyse bonus offers looking beyond the headline figures. The best bonus isn't 
                  always the biggest – fair terms matter more.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Welcome bonus value and structure</li>
                  <li>Wagering requirements and whether they're achievable</li>
                  <li>Time limits for meeting requirements</li>
                  <li>Game contribution percentages</li>
                  <li>Maximum withdrawal limits from bonus winnings</li>
                  <li>Ongoing promotions and loyalty rewards</li>
                  <li>Clarity and transparency of terms</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">5</div>
              <div className="criteria-content">
                <h2>Payment Methods</h2>
                <div className="criteria-weight">Weight: 10%</div>
                <p>
                  Getting money in and out of your casino account should be easy, fast, and free of 
                  hidden fees. We test the full range of payment options.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Variety of deposit methods available to UK players</li>
                  <li>Withdrawal options and any restrictions</li>
                  <li>Processing times (actual, not just stated)</li>
                  <li>Fees for deposits and withdrawals</li>
                  <li>Minimum and maximum limits</li>
                  <li>Verification requirements and process</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">6</div>
              <div className="criteria-content">
                <h2>Customer Support</h2>
                <div className="criteria-weight">Weight: 10%</div>
                <p>
                  We test customer service across multiple channels, at different times, with various 
                  types of queries. Good support can make or break the player experience.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Support channels available (live chat, email, phone)</li>
                  <li>Operating hours and availability</li>
                  <li>Response times across all channels</li>
                  <li>Quality and helpfulness of responses</li>
                  <li>Knowledge of staff about games, bonuses, and policies</li>
                  <li>Language support for UK players</li>
                </ul>
              </div>
            </div>

            <div className="criteria-card">
              <div className="criteria-number">7</div>
              <div className="criteria-content">
                <h2>User Experience</h2>
                <div className="criteria-weight">Weight: 5%</div>
                <p>
                  A great casino should be easy to use on any device. We evaluate the overall experience 
                  from registration to playing to withdrawal.
                </p>
                <h4>What We Check:</h4>
                <ul>
                  <li>Website design and navigation</li>
                  <li>Mobile compatibility and app quality</li>
                  <li>Registration process simplicity</li>
                  <li>Account management features</li>
                  <li>Loading times and technical performance</li>
                  <li>Search and filtering options</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rating-explanation">
            <h2>Our Rating System</h2>
            <p>
              Based on our evaluation, each casino receives an overall rating out of 5. Here's what 
              the ratings mean:
            </p>
            <div className="rating-scale">
              <div className="rating-item">
                <span className="rating-stars">★★★★★</span>
                <span className="rating-range">4.5 - 5.0</span>
                <span className="rating-label">Exceptional</span>
                <p>Outstanding in all areas. Highly recommended.</p>
              </div>
              <div className="rating-item">
                <span className="rating-stars">★★★★☆</span>
                <span className="rating-range">4.0 - 4.4</span>
                <span className="rating-label">Excellent</span>
                <p>Very strong performer with minor areas for improvement.</p>
              </div>
              <div className="rating-item">
                <span className="rating-stars">★★★☆☆</span>
                <span className="rating-range">3.0 - 3.9</span>
                <span className="rating-label">Good</span>
                <p>Solid choice for most players, some notable weaknesses.</p>
              </div>
              <div className="rating-item">
                <span className="rating-stars">★★☆☆☆</span>
                <span className="rating-range">2.0 - 2.9</span>
                <span className="rating-label">Average</span>
                <p>Meets basic standards but has significant drawbacks.</p>
              </div>
              <div className="rating-item">
                <span className="rating-stars">★☆☆☆☆</span>
                <span className="rating-range">Below 2.0</span>
                <span className="rating-label">Not Recommended</span>
                <p>Does not meet our standards. We do not feature these casinos.</p>
              </div>
            </div>
          </div>

          <div className="editorial-independence">
            <h2>Editorial Independence</h2>
            <p>
              As an affiliate website, we earn commission when players sign up to casinos through our links. 
              However, we maintain strict editorial independence:
            </p>
            <ul>
              <li>Our editorial and commercial teams operate separately</li>
              <li>Commission rates never influence our ratings or recommendations</li>
              <li>We apply the same evaluation criteria to all casinos</li>
              <li>Negative findings are always reported, even for commercial partners</li>
              <li>We regularly re-evaluate casinos and update ratings when needed</li>
            </ul>
            <p>
              Our reputation depends on providing trustworthy information. We would never compromise 
              our integrity for short-term commercial gain.
            </p>
          </div>

          <div className="review-cta">
            <h2>Questions About Our Process?</h2>
            <p>
              We're always happy to discuss our methodology. If you have questions or feedback 
              about how we review casinos, please get in touch.
            </p>
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowWeReview;
